import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link, NavLink } from "../i18n";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../i18n";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", id: "home", label: t("nav.home") },
    { to: "/inventory", id: "inventory", label: t("nav.inventory") },
    { to: "/dismantling", id: "dismantling", label: t("nav.dismantling") },
    { to: "/machinery", id: "machinery", label: t("nav.machinery") },
    { to: "/about", id: "about", label: t("nav.about") },
    { to: "/contact", id: "contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open ? "bg-black/60 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
          <Logo />
          <span className="font-display font-extrabold tracking-[0.25em] text-lg text-slate-100 group-hover:text-[#4499F7] transition-colors">
            KATACO
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors hover:text-[#4499F7] ${
                  isActive ? "text-[#4499F7]" : "text-slate-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <button
            data-testid="lang-switcher-btn"
            onClick={() => setLang(lang === "en" ? "ro" : "en")}
            className="font-mono-num text-xs tracking-widest text-slate-300 hover:text-[#4499F7] transition-colors border border-white/10 rounded-full px-3 py-1.5"
          >
            {lang === "en" ? "RO" : "EN"}
          </button>
          <a
            data-testid="header-call-btn"
            href="tel:+40723250260"
            className="flex items-center gap-2 bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-[#6FB1FA] transition-colors"
          >
            <Phone size={15} strokeWidth={2.5} />
            +40 723 250 260
          </a>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-200 p-2"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-black/80 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`mobile-nav-${l.id}-link`}
                  className="font-display text-2xl font-semibold text-slate-200 hover:text-[#4499F7] transition-colors"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-4 pt-2">
                <button
                  data-testid="mobile-lang-switcher-btn"
                  onClick={() => setLang(lang === "en" ? "ro" : "en")}
                  className="font-mono-num text-xs tracking-widest text-slate-300 border border-white/10 rounded-full px-3 py-1.5"
                >
                  {lang === "en" ? "RO" : "EN"}
                </button>
                <a data-testid="mobile-call-btn" href="tel:+40723250260" className="text-[#4499F7] font-semibold text-sm">
                  +40 723 250 260
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
